/**
 * PostHog Analytics Service
 * 
 * Provides helper functions for tracking events in PostHog.
 * In React components, use the usePostHog() hook directly.
 * For use outside React components (e.g., Redux sagas), this will use the global instance.
 */

/**
 * Get PostHog instance from global or parameter
 */
const getPostHog = (posthog?: any): any => {
  // First try the passed instance
  if (posthog && typeof posthog.capture === 'function') {
    return posthog;
  }
  // Fall back to global instance (works on native)
  if (typeof global !== 'undefined' && (global as any).posthog) {
    const globalPh = (global as any).posthog;
    if (globalPh && typeof globalPh.capture === 'function') {
      return globalPh;
    }
  }
  // Fall back to window instance (works on web)
  if (typeof window !== 'undefined' && (window as any).posthog) {
    const windowPh = (window as any).posthog;
    if (windowPh && typeof windowPh.capture === 'function') {
      return windowPh;
    }
  }
  return null;
};

/**
 * Track an event with PostHog
 * @param eventName - The name of the event to track
 * @param properties - Optional properties to attach to the event
 * @param posthog - Optional PostHog instance (if not provided, uses global instance)
 */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, any>,
  posthog?: any
): void => {
  try {
    const ph = getPostHog(posthog);
    if (ph && typeof ph.capture === 'function') {
      ph.capture(eventName, properties);
      console.log(`📊 [POSTHOG] Event tracked: ${eventName}`, properties);
    } else {
      console.warn(`⚠️ [POSTHOG] PostHog instance not available. Event: ${eventName}`, {
        hasPosthog: !!posthog,
        hasGlobal: typeof global !== 'undefined' && !!(global as any).posthog,
        posthogType: typeof posthog,
        phType: typeof ph,
      });
    }
  } catch (error) {
    console.error(`❌ [POSTHOG] Error tracking event ${eventName}:`, error);
  }
};

/**
 * Identify a user in PostHog
 * @param userId - The user's unique identifier
 * @param properties - Optional user properties
 * @param posthog - Optional PostHog instance (if not provided, uses global instance)
 */
export const identifyUser = (
  userId: string,
  properties?: Record<string, any>,
  posthog?: any
): void => {
  try {
    const ph = getPostHog(posthog);
    if (ph) {
      ph.identify(userId, properties);
      console.log(`👤 [POSTHOG] User identified: ${userId}`, properties);
    } else {
      console.warn(`⚠️ [POSTHOG] PostHog instance not available. Cannot identify user: ${userId}`);
    }
  } catch (error) {
    console.error(`❌ [POSTHOG] Error identifying user ${userId}:`, error);
  }
};

/**
 * Set user properties that will be sent with every event
 * @param properties - Properties to set for the user
 * @param posthog - Optional PostHog instance (if not provided, uses global instance)
 */
export const setUserProperties = (
  properties: Record<string, any>,
  posthog?: any
): void => {
  try {
    const ph = getPostHog(posthog);
    if (ph) {
      ph.setPersonProperties(properties);
      console.log(`📝 [POSTHOG] User properties set:`, properties);
    } else {
      console.warn(`⚠️ [POSTHOG] PostHog instance not available. Cannot set user properties.`);
    }
  } catch (error) {
    console.error(`❌ [POSTHOG] Error setting user properties:`, error);
  }
};

/**
 * Reset PostHog (useful for logout)
 * @param posthog - Optional PostHog instance (if not provided, uses global instance)
 */
export const resetPostHog = (posthog?: any): void => {
  try {
    const ph = getPostHog(posthog);
    if (ph) {
      ph.reset();
      console.log(`🔄 [POSTHOG] PostHog reset`);
    }
  } catch (error) {
    console.error(`❌ [POSTHOG] Error resetting PostHog:`, error);
  }
};

